import React, { useRef, useState } from "react";
import { useToast } from "@finitless/shared/hooks";

const ImageUploader = ({
  onImageChange,
  className = "",
  maxSize = 2097152, // 2MB
  accept = "image/png, image/jpeg, image/jpg",
  children,
  disabled = false,
  "aria-label": ariaLabel,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const toast = useToast();

  const handleFile = (file) => {
    if (!file) return;

    if (file.size > maxSize) {
      toast.error("File too large", {
        description: `Image must be less than ${Math.round(
          maxSize / 1024 / 1024,
        )}MB`,
      });
      return;
    }

    // Basic type check
    if (
      accept !== "*" &&
      !accept.includes(file.type) &&
      !file.type.startsWith("image/")
    ) {
      toast.error("Invalid file type", {
        description: "Please upload a valid image file",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (onImageChange) {
        onImageChange(file, e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleKeyDown = (event) => {
    if (disabled || (event.key !== "Enter" && event.key !== " ")) return;

    event.preventDefault();
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`${className} ${
        isDragging ? "border-primary bg-accent ring-primary/20 ring-2" : ""
      } ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      } focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none`}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInputChange}
        accept={accept}
        className="hidden"
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={(event) => event.stopPropagation()}
      />
      {children}
    </div>
  );
};

export default ImageUploader;
